import type { ReactNode } from "react";
import { Button as DovaButton } from "@codel/dova-ds";

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "secondary";
  size?: "md" | "lg" | "xl";
  disabled?: boolean;
}

/**
 * Adapter — preserves PR #938's Button call-site API (`primary`/`secondary`,
 * `md`/`lg`, full-width by default) while rendering dova-ds `Button` underneath
 * (the "collapse onto dova-ds" decision, Q1 = do-it-right).
 *
 * Mapping:
 *   - #938 `secondary` → dova-ds `outline` (surface + hairline border).
 *   - #938 buttons are full-width → dova-ds `full`.
 *   - sizes pass through (#938 `md`/`lg` map to dova-ds `md`/`lg`).
 *   - `xl` (48px) is dova-ds's onboarding size and the DEFAULT here, matching
 *     the therapist wizard. Every screen in this flow is one question with one
 *     way forward, so the CTA is the point of the screen rather than one
 *     control among many. It is the default rather than 13 explicit props
 *     because the next step added would otherwise quietly land at `lg`.
 */
export function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  size = "xl",
  disabled = false,
}: ButtonProps) {
  return (
    <DovaButton
      type={type}
      onClick={onClick}
      disabled={disabled}
      variant={variant === "secondary" ? "outline" : "primary"}
      size={size}
      full
    >
      {children}
    </DovaButton>
  );
}
